import { useEffect, useMemo } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query'
import { getCarPark, getFleetSummary } from '../api/newApi'
import { useChosenCompany, useChosenConsolidateInvoice } from '../context/UserProvider'
import type { SimpleVehicleProps } from '../pages/cars/components/V3/types/types'
import type { FleetSummary } from '../types'

export const ALL_CONSOLIDATE_CODES = '_ALL_'

export const FLEET_CHUNK_SIZE = 100

export const FLEET_FILTERS = [
  'all',
  'order',
  'active',
  'administrated',
  'expiring',
  'closed',
  'unresolved',
  'overmileage',
] as const

export type FleetFilter = (typeof FLEET_FILTERS)[number]

export const FLEET_DEFAULT_FILTER: FleetFilter = 'active'

export const toFleetFilter = (value?: string | null): FleetFilter =>
  FLEET_FILTERS.includes(value as FleetFilter) ? (value as FleetFilter) : FLEET_DEFAULT_FILTER

const FLEET_STALE_TIME = 5 * 60 * 1000

export const useFleet = (consolidationCode: string = ALL_CONSOLIDATE_CODES, enabled = true) => {
  const company = useChosenCompany()
  const consolidateInvoice = useChosenConsolidateInvoice()
  const companyId = company?.id

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['carPark', companyId, consolidationCode],
    queryFn: async () =>
      (await getCarPark({
        companyId,
        consolidationCode,
      })) as any,
    enabled: enabled && !!companyId && !!consolidateInvoice,
  })

  return {
    cars: (data?.cars ?? null) as SimpleVehicleProps[] | null,
    isLoading,
    isFetching,
    refetch,
  }
}

const fetchFleetBatch = async ({
  companyId,
  consolidationCode,
  filter,
  pageNo,
}: {
  companyId: string
  consolidationCode: string
  filter: FleetFilter
  pageNo: number
}) => {
  const page = (await getCarPark({
    companyId,
    consolidationCode,
    filter,
    pageNo,
    pageSize: FLEET_CHUNK_SIZE,
  })) as any

  return (page?.cars ?? []) as SimpleVehicleProps[]
}

export const useFleetCars = (
  filter: FleetFilter = FLEET_DEFAULT_FILTER,
  totalCount?: number,
  consolidationCode: string = ALL_CONSOLIDATE_CODES,
) => {
  const queryClient = useQueryClient()
  const company = useChosenCompany()
  const consolidateInvoice = useChosenConsolidateInvoice()
  const companyId = company?.id

  const { data, isLoading, isFetching, isPreviousData, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useInfiniteQuery<SimpleVehicleProps[]>({
      queryKey: ['carPark', companyId, consolidationCode, filter],
      queryFn: async ({ pageParam = 1 }) =>
        await fetchFleetBatch({ companyId, consolidationCode, filter, pageNo: pageParam }),
      getNextPageParam: (lastBatch, batches) => {
        if (lastBatch.length < FLEET_CHUNK_SIZE) return undefined
        const loaded = batches.reduce((count, batch) => count + batch.length, 0)
        if (totalCount && loaded >= totalCount) return undefined
        return batches.length + 1
      },
      enabled: !!companyId && !!consolidateInvoice,
      keepPreviousData: true,
      staleTime: FLEET_STALE_TIME,
    })

  useEffect(() => {
    if (isPreviousData || isFetchingNextPage || !hasNextPage) return
    fetchNextPage()
  }, [isPreviousData, isFetchingNextPage, hasNextPage, fetchNextPage])

  const cars = useMemo(() => (data ? data.pages.flat() : null), [data])

  const resetFilter = (next: FleetFilter) => queryClient.resetQueries(['carPark', companyId, consolidationCode, next])

  return {
    cars,
    resetFilter,
    loadedCount: cars?.length ?? 0,
    isLoading,
    isLoadingMore: !isPreviousData && (isFetchingNextPage || hasNextPage === true),
    isRefreshing: isFetching && !isFetchingNextPage,
    refetch,
  }
}

export const useFleetSummary = (consolidationCode: string = ALL_CONSOLIDATE_CODES) => {
  const company = useChosenCompany()
  const consolidateInvoice = useChosenConsolidateInvoice()
  const companyId = company?.id

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['fleetSummary', companyId, consolidationCode],
    queryFn: async () =>
      (await getFleetSummary({
        companyId,
        consolidationCode,
      })) as any,
    enabled: !!companyId && !!consolidateInvoice,
    staleTime: FLEET_STALE_TIME,
  })

  return {
    summary: (data ?? null) as FleetSummary | null,
    isLoading,
    isFetching,
    refetch,
  }
}
